import React, { ChangeEvent, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  CardMedia,
  IconButton,
  Input,
  TextField,
  CircularProgress,
  Box
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { uploadService } from "../../api/services/uploadService";
import { useCreatePromotion, useUpdatePromotion, useGetPromotion } from "../../api/hooks/usePromotion";
import { showSuccess, showError } from "../../helpers/toast";

const AddPromotion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const promotionId = location.state?.id;
  const isAddPromotion = !promotionId;

  const [image, setImage] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [redirectionLink, setRedirectionLink] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const { data: promotionData, isLoading: isLoadingPromotion } = useGetPromotion(promotionId, {
      enabled: !!promotionId
  });

  useEffect(() => {
      if (promotionData?.data) {
          const promo = promotionData.data;
          setImage(promo.image);
          setRedirectionLink(promo.redirectUrl);
      }
  }, [promotionData]);

  const { mutate: createPromotion, isPending: isCreating } = useCreatePromotion({
      onSuccess: () => {
          showSuccess("Promotion created successfully");
          navigate("/manage-promotions");
      },
      onError: (error: any) => {
          showError(error?.response?.data?.message || "Failed to create promotion");
      }
  });

  const { mutate: updatePromotion, isPending: isUpdating } = useUpdatePromotion({
      onSuccess: () => {
          showSuccess("Promotion updated successfully");
          navigate("/manage-promotions");
      },
      onError: (error: any) => {
          showError(error?.response?.data?.message || "Failed to update promotion");
      }
  });

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const selectedFile = files[0];
      if (!selectedFile.type.includes("image")) {
        showError("This field only accepts images.");
        return;
      }
      setFile(selectedFile);
      setImage(URL.createObjectURL(selectedFile));
    }
  };

  const handleSave = async () => {
      if (!image && !file) {
          showError("Please upload an image");
          return;
      }
      if (!redirectionLink) {
          showError("Please enter a redirection link");
          return;
      }

      try {
          let imageUrl = image;
          if (file) {
              setIsUploading(true);
              const formData = new FormData();
              formData.append('file', file);
              
              const response = await uploadService.uploadFile(formData);
              if (typeof response.data === 'string') {
                  imageUrl = response.data;
              } else if (response.data && typeof response.data === 'object' && 'url' in response.data) {
                  imageUrl = response.data.url || '';
              }
              setIsUploading(false);
          }

          const payload = {
              image: imageUrl,
              title: redirectionLink,
              redirectUrl: redirectionLink
          };

          if (isAddPromotion) {
              createPromotion(payload);
          } else {
              updatePromotion({ id: promotionId, data: payload });
          }

      } catch (error) {
          setIsUploading(false);
          showError("Failed to upload image");
      }
  };

  if (isLoadingPromotion) {
      return (
          <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
              <CircularProgress />
          </Box>
      );
  }

  const isLoading = isCreating || isUpdating || isUploading;

  return (
    <>
      <div className="main_title">
        <h1>{isAddPromotion ? "Add " : "Edit "} Promotion</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-promotions")}>
            Promotion Management
          </button>{" "}
          - {isAddPromotion ? "Add " : "Edit "} Promotion
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_100">
                <label>Upload Image</label>
                  {image ? (
                    <div className="upload_image v2 no_radius" style={{ width: 'fit-content' }}>
                      <div className="upload_image_holder">
                        <figure>
                          <CardMedia
                            component="img"
                            image={image}
                            alt="Uploaded promotion"
                          />
                        </figure>
                        <IconButton
                          aria-label="Remove uploaded image"
                          onClick={() => {
                            setImage("");
                            setFile(null);
                          }}
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      </div>
                    </div>
                  ) : (
                    <label
                      className="upload_image v2 no_radius"
                      htmlFor="icon-button-file"
                      style={{ width: 'fit-content' }}
                    >
                      <Input
                        sx={{ display: "none" }}
                        id="icon-button-file"
                        type="file"
                        inputProps={{ accept: "image/*" }}
                        onChange={handleImageChange}
                      />
                      <span className="upload_image_holder">
                        <figure className="add_img">
                          <img
                            src="/static/images/placeholder.png"
                            alt="placeholder"
                          />
                          <span className="upload_img_icon">
                            <img
                              src="/static/images/edit2_icon.svg"
                              alt="Upload"
                            />
                          </span>
                        </figure>
                      </span>
                    </label>
                  )}
            </div>

            <div className="control_group w_100">
              <label htmlFor="redirectionLink">Redirection Link</label>
              <TextField
                id="redirectionLink"
                placeholder="Enter Redirection Link"
                hiddenLabel
                fullWidth
                value={redirectionLink}
                onChange={(e) => setRedirectionLink(e.target.value)}
              />
            </div>
          </div>

          <div className="form_btn">
            <Button 
                className="btn btn_primary" 
                onClick={handleSave}
                disabled={isLoading}
            >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddPromotion;
